import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import {
  BASE_PERMISSIONS,
  baseAdministrationActions,
  baseAdministrationSelectors,
  BaseAdministrationService,
  BaseButtonSize,
  BaseCommonFormComponent,
  BaseCoreState,
  BaseHelpersService,
  BaseRolesService,
  BaseSharedFormBuilderService,
  companyMainFormKey,
  createCompanyMainFormGroup,
  CustomerMainFormInterface,
  CustomerMainInfoUpdateRequest,
  CustomerResponse,
} from 'base';
import { CompanySettingsFormInterface } from '../../_interfaces/company-settings.interface';

@Component({
  selector: 'base-customer-administration-master-data',
  templateUrl: './base-customer-master-data.component.html',
})
export class BaseCustomerAdministrationMasterDataComponent
  extends BaseCommonFormComponent
  implements OnInit, OnDestroy
{
  customer: CustomerResponse | undefined;
  buttonSizes = BaseButtonSize;
  submitted = false;
  isEditMode = false;
  modalRef: NgbModalRef;
  mainForm: FormGroup<CustomerMainFormInterface>;
  companySettingsForm: FormGroup<CompanySettingsFormInterface>;
  hasMasterDataAccess: boolean;
  destroy$ = new Subject();

  constructor(
    private readonly sharedFormBuilderService: BaseSharedFormBuilderService,
    private administrationService: BaseAdministrationService,
    private rolesService: BaseRolesService,
    private fb: NonNullableFormBuilder,
    protected store: Store<BaseCoreState>
  ) {
    super(store);

    this.mainForm = this.sharedFormBuilderService.sharedGroup(companyMainFormKey, createCompanyMainFormGroup);
    this.mainForm.updateValueAndValidity();
  }

  ngOnInit(): void {
    this.hasMasterDataAccess = this.rolesService.hasCustomerFeaturePermission(
      BASE_PERMISSIONS.FEATURE_ADMIN_MASTER_DATA
    );
    this.initCompanySettingsForm();

    this.store
      .select(baseAdministrationSelectors.getCustomerData)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.ok),
        distinctUntilChanged(
          (prevState, currState) => JSON.stringify(prevState.data) === JSON.stringify(currState.data)
        ),
        map((state) => state.data as CustomerResponse)
      )
      .subscribe((result) => {
        this.customer = result;
        this.companySettingsForm.patchValue(
          {
            assignmentsLocationOnly: this.customer.assignmentsLocationOnly,
            equalPayLocationOnly: this.customer.equalPayLocationOnly,
            ratingsLocationOnly: this.customer.ratingsLocationOnly,
            maximumLeasePeriodDifference: this.customer.maximumLeasePeriodDifference,
            equalPay: this.customer.equalPay,
          },
          { emitEvent: false }
        );

        this.mainForm.controls.contact.patchValue({
          ...this.customer.contact,
          ...this.customer.primaryData,
          vatId: this.customer.vatId,
        });
        this.mainForm.controls.businessAddress.patchValue(this.customer.businessAddress);
        this.mainForm.controls.billingAddress.patchValue({
          ...this.customer.billingContact.billingAddress,
          legalName: this.customer.billingContact.legalName,
          email: this.customer.billingContact.email,
        });

        this.initState = BaseHelpersService.convertObjToTuple(this.customer);
      });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.destroy$.next(true);
  }

  fillBillingAddress(): void {
    const businessAddressFormValue = this.mainForm.controls.businessAddress.value;

    this.mainForm.controls.billingAddress.patchValue({
      city: businessAddressFormValue.city,
      countryCode: businessAddressFormValue.countryCode,
      postCode: businessAddressFormValue.postCode,
      street: businessAddressFormValue.street,
    });
  }

  saveChanges(): void {
    this.submitted = true;
    this.mainForm.markAllAsTouched();
    if (this.customer && this.mainForm.valid) {
      const preparedData = this.administrationService.prepareCustomerUpdateModel(this.mainForm.value);
      this.store.dispatch(
        baseAdministrationActions.updateCustomerInfo({
          payload: {
            ...preparedData,
            assignmentsLocationOnly: this.customer.assignmentsLocationOnly,
            equalPayLocationOnly: this.customer.equalPayLocationOnly,
            ratingsLocationOnly: this.customer.ratingsLocationOnly,
            maximumLeasePeriodDifference: this.customer.maximumLeasePeriodDifference,
            equalPay: this.customer.equalPay,
          },
        })
      );
      this.isEditMode = false;
    }
  }

  updateCompanySettings(): void {
    if (!this.customer) {
      return;
    }

    this.store.dispatch(
      baseAdministrationActions.updateCustomerInfo({
        payload: {
          ...this.companySettingsForm.value,
          billingContact: this.customer.billingContact,
          businessAddress: this.customer.businessAddress,
          contact: this.customer.contact,
          primaryData: this.customer.primaryData,
          vatId: this.customer.vatId,
        } as CustomerMainInfoUpdateRequest,
      })
    );
  }

  private initCompanySettingsForm(): void {
    this.companySettingsForm = this.fb.group({
      assignmentsLocationOnly: this.fb.control(false),
      equalPayLocationOnly: this.fb.control(false),
      ratingsLocationOnly: this.fb.control(false),
      maximumLeasePeriodDifference: this.fb.control(1, [Validators.required, Validators.min(1)]),
      equalPay: this.fb.control(1, [Validators.required, Validators.min(1)]),
    });
  }
}
